import { Injectable } from '@angular/core';
import { BaseProductService } from './base-product.service';
import { IProductBase, IProductCreationParams, IProductDeleteParams, IProductReservationBase, IProductReservationParams } from 'model/product.interface';
import { keyRandomUnique } from 'utils/helpers';

@Injectable({
  providedIn: 'root'
})
export class BoatProductService extends BaseProductService {

  private readonly CATEGORY_ID = 3;
  private readonly CATEGORY_NAME = 'BOATS';

  private form: any;

  /**
   * Arma el item de itinerario de un barco.
   * - item / product: el resultado de la búsqueda (copia del barco).
   * - othersPerCategory.option: la semana elegida (con su precio y fechas).
   * - othersPerCategory.extras: extras cobrados (obligatorios + opcionales marcados).
   */
  createProduct(params: IProductCreationParams): IProductBase {
    const { item, product, payload, othersPerCategory } = params;

    if (!this.form) {
      this.form = JSON.parse(localStorage.getItem('form'));
    }

    const boat = { ...item };
    const option = othersPerCategory.option;
    const extras: any[] = othersPerCategory.extras || [];
    const searchPayload = Array.isArray(payload) ? payload[0] : payload;

    const onlineExtras = extras.filter(extra => !this.paysOnSite(extra));
    const onSiteExtras = extras.filter(extra => this.paysOnSite(extra));

    const rentPrice = Number(option.priceGross) || 0;
    const extrasOnline = this.sumExtras(onlineExtras);
    const extrasOnSite = this.sumExtras(onSiteExtras);
    const totalPrice = Number((rentPrice + extrasOnline).toFixed(2));

    boat.id = option.optionId ?? option.id ?? product.id;
    boat.categoryId = this.CATEGORY_ID;
    boat.categoryName = this.CATEGORY_NAME;
    boat.currency = option.currency || product.additionalInfo?.currency;
    boat.title = product.additionalInfo?.boat?.boatName || product.title;
    boat.productName = boat.title;
    boat.updateDate = new Date();
    boat.productKey = keyRandomUnique();
    boat.form = this.form;
    boat.payload = payload;
    boat.searchId = othersPerCategory.searchId;
    boat.images = product.additionalInfo?.images;

    boat.arrivalDate = option.dateFrom;
    boat.departureDate = option.dateTo;
    boat.nights = option.days;
    boat.adults = searchPayload?.boats?.adults ?? 0;
    boat.childrens = searchPayload?.boats?.children?.length ?? 0;

    // Datos para reservar con el proveedor: la opción (semana) y los extras elegidos.
    boat.bookParam = option.bookParam ?? product.bookParam;
    // La opción elegida ya lleva su bookParam y extras; se quitan de additionalInfo/boatOption
    // las listas de todas las semanas y extras para no guardar el resultado completo en el itinerario.
    boat.boatOption = { ...option, extras: [] };
    boat.additionalInfo = { ...product.additionalInfo, options: [], extras: [] };
    boat.selectedExtras = extras;
    boat.cancellationPolicies = product.additionalInfo?.cancellationPolicy || [];
    boat.expiration_date = null;

    // El carrito pinta Reembolsable / No Reembolsable según rateClass.
    const refundable = Array.isArray(boat.cancellationPolicies) && boat.cancellationPolicies.length > 0;
    boat.rateClass = refundable ? 'R' : 'NR';
    boat.rateClassId = boat.rateClass;

    boat.netPrice = totalPrice;
    boat.totalPrice = totalPrice;
    boat.totalPriceProf = totalPrice;
    boat.netPriceFreeTax = totalPrice;
    boat.taxes = [];
    boat.taxPrice = 0;
    boat.tax = 0;
    boat.blickoins = 0;
    boat.creditBlickoins = 0;
    boat.priceOnSite = extrasOnSite;
    boat.serviceExtra = extras.map(extra => ({
      name: extra.title,
      price: this.extraAmount(extra)
    }));

    boat.payloadUpdate = this.buildPayloadUpdate(searchPayload, boat, option, othersPerCategory);
    boat.destination = this.itineraryService.getDestinationInfo(this.form, boat);

    return boat as IProductBase;
  }

  createReserve(params: IProductReservationParams): IProductReservationBase {
    const { item } = params;
    return item as IProductReservationBase;
  }

  deleteProduct(params: IProductDeleteParams): IProductBase {
    throw new Error('Method not implemented.');
  }

  validateProduct(item: any): boolean {
    throw new Error('Method not implemented.');
  }

  /**
   * Es el payload del checkAvail: se envía tal cual a search/products al reservar.
   * Los extras obligatorios ya van dentro del bookingCode; solo se mandan los opcionales elegidos.
   */
  private buildPayloadUpdate(searchPayload: any, boat: any, option: any, others: any): any {
    const optionalExtras = (others.extras || [])
      .filter((extra: any) => String(extra?.ismandatory) !== 'true')
      .map((extra: any) => ({ extraid: extra.extraid, optionid: extra?.options?.[0]?.optionid }));

    return {
      products: [
        {
          providerId: boat.providerId,
          productId: boat.productId,
          categoriesId: [this.CATEGORY_ID],
          destination: searchPayload?.destination,
          arrivalDate: option.dateFrom,
          departureDate: option.dateTo,
          agencyId: others.userService?._agencyID ?? searchPayload?.agencyId,
          id: boat.id,
          boats: {
            bookingCode: boat.bookParam,
            adults: searchPayload?.boats?.adults ?? 0,
            children: searchPayload?.boats?.children ?? [],
            extras: optionalExtras
          }
        }
      ]
    };
  }

  private extraAmount(extra: any): number {
    return Number(extra?.options?.[0]?.netcost?.amount) || 0;
  }

  private paysOnSite(extra: any): boolean {
    return extra?.options?.[0]?.paymentOffice === true;
  }

  private sumExtras(extras: any[]): number {
    return extras.reduce((total, extra) => total + this.extraAmount(extra), 0);
  }
}
